import { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Eye, EyeOff, RefreshCw, FileText } from 'lucide-react';
import type { BlogPost } from './types';

interface BlogManagementProps {
  onEdit: (post: BlogPost) => void;
  onNew: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const CATEGORIES = ['Getting Started', 'Comparison', 'Advanced', 'Tips', 'Case Studies'];

const CARD = 'bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm';
const INPUT = 'bg-white/[0.05] border border-white/[0.1] rounded-xl px-3.5 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-all';
const BTN_PRIMARY = 'inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors';
const BTN_SECONDARY = 'inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors';
const BTN_DANGER = 'inline-flex items-center gap-1.5 bg-red/10 hover:bg-red/20 border border-red/20 text-red rounded-lg px-3 py-1.5 text-xs font-medium transition-colors';

function BlogManagement({ onEdit, onNew }: BlogManagementProps) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) throw new Error('Admin token not found. Please log in first.');

      const response = await fetch(`${API_URL}/api/blog/admin/posts`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });

      const responseText = await response.text();
      let data;
      try { data = JSON.parse(responseText); }
      catch { throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`); }

      if (!response.ok) throw new Error(data.error || `Failed to fetch posts (${response.status})`);
      setPosts(data.posts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/blog/admin/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to delete post');
      setPosts(posts.filter(p => p._id !== postId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    }
  };

  const handleTogglePublished = async (postId: string, currentStatus: boolean) => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/blog/admin/posts/${postId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ published: !currentStatus }),
      });
      if (!response.ok) throw new Error('Failed to update post status');
      setPosts(posts.map(p => p._id === postId ? { ...p, published: !currentStatus } : p));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update post status');
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.titleEs.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || post.category === filterCategory;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'published' && post.published) ||
      (filterStatus === 'draft' && !post.published);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div className="w-8 h-8 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
        <p className="text-white/40 text-sm">Loading blog posts…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Blog Management</h1>
        <div className="flex items-center gap-2">
          <button onClick={fetchPosts} className={BTN_SECONDARY}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={onNew} className={BTN_PRIMARY}>
            <Plus size={15} /> New Article
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red/10 border border-red/20 text-red rounded-xl px-4 py-3 text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button onClick={fetchPosts} className="text-red hover:text-red text-xs underline underline-offset-2 shrink-0">
            Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className={`${CARD} p-4 flex flex-wrap gap-3`}>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            placeholder="Search articles…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${INPUT} pl-9 w-full`}
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className={`${INPUT} min-w-[160px]`}
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={`${INPUT} min-w-[130px]`}
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-xs text-white/40">
        <span>{filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span>{posts.filter(p => p.published).length} published</span>
        <span>·</span>
        <span>{posts.filter(p => !p.published).length} draft{posts.filter(p => !p.published).length !== 1 ? 's' : ''}</span>
      </div>

      {/* Posts list */}
      {filteredPosts.length === 0 ? (
        <div className={`${CARD} flex flex-col items-center justify-center py-16 gap-3`}>
          <FileText size={32} className="text-white/15" />
          <p className="text-white/40 text-sm">No articles found matching your criteria.</p>
          <button onClick={onNew} className={BTN_PRIMARY}>
            <Plus size={14} /> Create First Article
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map(post => (
            <div key={post._id} className={`${CARD} flex gap-4 p-4 hover:bg-white/[0.06] transition-colors`}>
              {/* Thumbnail */}
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-white/[0.07]"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-white/[0.04] border border-white/[0.07] flex items-center justify-center flex-shrink-0">
                  <FileText size={22} className="text-white/20" />
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 mb-1">
                  <h3 className="text-white font-semibold text-sm leading-snug truncate">{post.title}</h3>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    post.published
                      ? 'bg-green/15 text-green'
                      : 'bg-orange/15 text-orange'
                  }`}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <p className="text-white/45 text-xs leading-relaxed line-clamp-2 mb-2">{post.excerpt}</p>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-white/30">
                  {post.category && (
                    <span className="px-2 py-0.5 bg-primary/10 text-primary/70 rounded-full">{post.category}</span>
                  )}
                  {post.readTime && <span>{post.readTime}</span>}
                  <span>{post.viewCount ?? 0} views</span>
                  <span>Updated {new Date(post.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => onEdit(post)} className={BTN_SECONDARY}>
                  <Edit2 size={12} /> Edit
                </button>
                <button
                  onClick={() => handleTogglePublished(post._id, post.published)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border ${
                    post.published
                      ? 'bg-orange/10 hover:bg-orange/20 border-orange/20 text-orange'
                      : 'bg-green/10 hover:bg-green/20 border-green/20 text-green'
                  }`}
                >
                  {post.published ? <EyeOff size={12} /> : <Eye size={12} />}
                  {post.published ? 'Unpublish' : 'Publish'}
                </button>
                <button onClick={() => handleDelete(post._id)} className={BTN_DANGER}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BlogManagement;
